<template>
  <div class="step-pane fade-in">
    <div class="cover-upload-wrapper">
      <el-upload
        v-if="imgOssUrl !== ''"
        class="avatar-uploader"
        :action="imgOssUrl"
        :headers="imgHeaders"
        :data="imgData"
        :show-file-list="false"
        :before-upload="beforeCoverUpload"
        :on-success="handleManualCoverSuccess"
      >
        <div v-if="coverUrl" class="cover-preview">
          <img :src="coverUrl" class="avatar">
          <div class="cover-mask">
            <i class="el-icon-edit" />
            <span>更换封面</span>
          </div>
        </div>
        <div v-else class="uploader-placeholder">
          <i class="el-icon-plus placeholder-icon" />
          <span class="placeholder-text">上传封面</span>
        </div>
      </el-upload>

      <div class="cover-info-text">
        <div class="tip-title"><i class="el-icon-info" /> 封面设置说明：</div>
        <p class="tip-item">① 优质的封面能帮您吸引更多观众点击。</p>
        <p class="tip-item">② 视频上传后，系统会自动截取第一帧作为推荐封面，若不满意可点击左侧随时更换。</p>
      </div>
    </div>
    <div v-if="!isSingle" class="step-actions">
      <el-button type="primary" size="medium" @click="$emit('next')">下一步：填写基本信息 <i class="el-icon-arrow-right" /></el-button>
    </div>
  </div>
</template>

<script>
import { updateVideoCover } from '@/api/vod'
import { getImageChannelInfo } from '@/api/file'

export default {
  name: 'StepUploadCover',
  props: {
    value: { type: Object, required: true }, // 表单数据 form
    videoFile: { type: File, default: null }, // 接收第一步传过来的视频文件
    coverUrl: { type: String, default: null }, // 外部同步的图片预览地址
    isEdit: { type: Boolean, default: false },
    isSingle: { type: Boolean, default: false }
  },
  data() {
    return {
      imgOssUrl: '',
      imgHeaders: { Authorization: '' },
      imgData: { clientSha256sum: null }
    }
  },
  watch: {
    // 监听视频文件的传入：一旦有新文件进来且处于发布模式，自动截帧
    videoFile: {
      handler(newFile) {
        if (newFile && !this.isEdit) {
          // 在配置初始化完毕后再执行截帧上传，防止配置未加载时发送请求
          this.ensureConfigAndAutoFrame(newFile)
        }
      },
      immediate: true
    }
  },
  created() {
    this.initCoverConfig()
  },
  methods: {
    async initCoverConfig() {
      try {
        const res = await getImageChannelInfo()
        if (res.code === 0) {
          this.imgOssUrl = res.data.ossUrl
          this.imgHeaders.Authorization = 'Bearer ' + res.data.token
          return true
        } else {
          this.$message.warning(res.msg)
        }
      } catch (e) {
        console.error('获取封面上传通道失败', e)
      }
      return false
    },
    // 确保配置加载完后再执行截帧逻辑
    async ensureConfigAndAutoFrame(file) {
      if (!this.imgOssUrl) {
        const success = await this.initCoverConfig()
        if (!success) return
      }
      this.generateLocalCover(file)
    },
    // 核心逻辑：本地异步视频自动截帧
    generateLocalCover(rawFile) {
      const video = document.createElement('video')
      video.src = URL.createObjectURL(rawFile)
      video.muted = true
      video.play()

      video.onloadeddata = () => {
        video.currentTime = 1 // 截取第一秒
      }

      video.onseeked = () => {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          canvas.toBlob((blob) => {
            if (blob) {
              this.handleAutoFrameUpload(blob)
            }
            URL.revokeObjectURL(video.src)
          }, 'image/jpeg', 0.85)
        }
      }
    },
    // 核心逻辑：自动截帧得到的 Blob 直接在此处上传至 OSS
    async handleAutoFrameUpload(blobFile) {
      const formData = new FormData()
      formData.append('file', blobFile)
      formData.append('clientSha256sum', '')

      fetch(this.imgOssUrl, {
        method: 'POST',
        headers: this.imgHeaders,
        body: formData
      })
        .then(res => res.json())
        .then(json => {
          if (json.code === 0) {
            this.value.coverFileId = json.data.uploadId
            // 更新父组件中的预览图
            this.$emit('update:coverUrl', URL.createObjectURL(blobFile))

            const videoCover = {
              videoId: this.value.videoId,
              coverFileId: this.value.coverFileId
            }
            updateVideoCover(videoCover).then(() => {
              this.$message.success('系统已自动为您截取视频首帧作为推荐封面')
            }).catch(error => {
              this.$message.error(error.message)
            })
          }
        })
    },
    beforeCoverUpload(file) {
      const isType = ['image/jpeg', 'image/png'].includes(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isType) this.$message.error('封面只能是 JPG 或 PNG 格式!')
      if (!isLt10M) this.$message.error('封面大小不能超过 10MB!')

      if (isType && isLt10M) {
        this.imgData.clientSha256sum = ''
      }
      return isType && isLt10M
    },
    // 手动更换封面的回调
    handleManualCoverSuccess(res, file) {
      if (res.code === 0) {
        const videoCover = { coverFileId: res.data.uploadId }
        if (this.isEdit) {
          videoCover.videoId = this.value.videoId
        } else {
          videoCover.videoId = this.value.videoId
          this.value.coverFileId = res.data.uploadId
          this.$emit('update:coverUrl', URL.createObjectURL(file.raw))
        }

        updateVideoCover(videoCover).then(() => {
          this.$message.success('视频封面更新成功')
        }).catch(error => {
          this.$message.error(error.message)
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.video-post-container {
  padding: 32px 20px;
  background-color: #f6f8fa;
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  .steps-wrapper {
    width: 100%;
    max-width: 880px; // 限制表单总宽度，防止宽屏下横向拉伸过长导致视觉松散
    border: 1px solid #eaeefb;
    border-radius: 12px;
    box-shadow: 0 6px 16px -4px rgba(0, 0, 0, 0.04);
    padding: 12px;
  }

  .custom-steps {
    margin-bottom: 40px;
    ::v-deep .el-step__title {
      font-size: 14px;
      font-weight: 500;
    }
  }

  // 步骤内容切换区域
  .step-content-router {
    min-height: 320px;
  }

  // 淡入动效
  .fade-in {
    animation: fadeIn 0.35s ease-in-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  // 统一的下方操作栏
  .step-actions {
    margin-top: 36px;
    padding-top: 20px;
    border-top: 1px solid #f0f2f5;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.center {
      justify-content: center;
    }
  }
}

/* 封面上传样式优化 */
.cover-upload-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 10px 0;

  .avatar-uploader {
    border: 2px dashed #e0e0e0;
    border-radius: 10px;
    cursor: pointer;
    overflow: hidden;
    width: 260px;
    height: 155px;
    background: #fafafa;
    transition: all 0.25s ease-in-out;
    flex-shrink: 0;

    &:hover {
      border-color: #409EFF;
      background: #f5f9ff;
    }
  }

  .uploader-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #909399;

    .placeholder-icon { font-size: 26px; margin-bottom: 8px; }
    .placeholder-text { font-size: 13px; }
  }

  .cover-preview {
    position: relative;
    width: 100%;
    height: 100%;
    .avatar { width: 100%; height: 100%; object-fit: cover; }
    .cover-mask {
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(29, 33, 41, 0.6); color: #fff;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      gap: 6px; font-size: 13px; opacity: 0; transition: opacity 0.2s ease;
    }
    &:hover .cover-mask { opacity: 1; }
  }

  .cover-info-text {
    flex: 1;

    .tip-title {
      font-size: 14px;
      font-weight: 500;
      color: #606266;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 4px;
      i { color: #409EFF; }
    }
    .tip-item { font-size: 13px; color: #8c929e; margin: 6px 0; line-height: 1.6; }
  }
}

/* 表单精细化调整 */
.custom-form {
  ::v-deep .el-form-item__label {
    padding-bottom: 6px;
    font-weight: 500;
    color: #303133;
  }

  .custom-radio-group {
    width: 100%;
    display: flex;
    ::v-deep .el-radio-button {
      flex: 1;
      .el-radio-button__inner { width: 100%; text-align: center; }
    }
  }

  .tags-select {
    ::v-deep .el-select__tags { max-height: 80px; overflow-y: auto; }
  }
}
</style>
